import Slider from 'rc-slider';
import { MarkObj } from 'rc-slider/lib/Marks';
import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';

interface RangeProps {
    value: number;
    name?: string;
    marks?: Record<string | number, ReactNode | MarkObj>;
    isShowLabel?: boolean;
    isShowMarks?: boolean;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    // eslint-disable-next-line no-unused-vars
    onChange: (value: number | number[], name: string | undefined) => void;
}

const SliderContainer = styled.div<{ isShowMark: boolean }>`
    ${({ theme: { colors }, isShowMark }) => `
        width: 100%;
        position: relative;
        padding: 0px 7px;
        & > .rc-slider {
            padding-top: ${!isShowMark ? `25px` : '0'};
            & > .rc-slider-handle {
                border: 3px solid ${colors.text.primary};
                opacity: 1;
                background: ${colors.background.primary};
                height: 13.5px;
                width: 13.5px;
            }
            & > .rc-slider-handle-dragging {
                box-shadow: none;
            }
            & > .rc-slider-rail {
                background: ${colors.border.light}
            }
            & > .rc-slider-track {
                background: ${colors.text.primary};
            }
            & > .rc-slider-mark {
                display: ${isShowMark ? `block` : `none`};
                .rc-slider-mark-text {
                    color: ${colors.text.tertiary};
                }
                .rc-slider-mark-text-active {
                    color: ${colors.text.primary};
                }
            }
            .rc-slider-step {
                 .rc-slider-dot {
                    height: 11.5px;
                    width: 11.5px;
                    background-color: ${colors.border.light};
                    border: 0px;
                    bottom: -3px;
                }
                .rc-slider-dot-active {
                    border: 0px;
                    background-color: ${colors.text.primary};
                }
            }
            
        }
        & > .rc-slider-disabled {
            background-color: transparent;
            & > .rc-slider-handle {
                border: 3px solid ${colors.text.disabled};
            }
            & > .rc-slider-rail, & > .rc-slider-track {
                background: ${colors.text.disabled}
            }
            .rc-slider-step {
                .rc-slider-dot, .rc-slider-dot-active {
                    background-color: ${colors.text.disabled};
                }
            }
            & > .rc-slider-mark {
                .rc-slider-mark-text, .rc-slider-mark-text-active  {
                    color: ${colors.text.disabled};
                }
            }
        }
    `}
`;

const ValueLabel = styled.span<{ value: number; max: number }>`
    ${({ theme: { typography, colors }, value, max }) => `
        padding-right: 0px 15px;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        font-size: ${typography.size.desktop.paragraph.p3};
        color: ${colors.text.primary};
        &:after {
            content: "";
            position: absolute;
            width: 2px;
            height: 2px;
            top: -1px;
            left: 50%;
        }
        left: calc(${(value * 100) / max}% + (${7 - value * 0.1 - value * 0.08}px))

    `}
`;

const Range: FC<RangeProps> = ({
    value,
    name,
    onChange,
    marks = { 0: 0, 25: 25, 50: 50, 75: 75, 100: 100 },
    isShowLabel = true,
    isShowMarks = false,
    min = 0,
    max = 100,
    step = 1,
    disabled = false,
}) => {
    const handleChange = (v: number | number[]) => {
        onChange(v, name);
    };

    return (
        <SliderContainer isShowMark={isShowMarks}>
            {isShowLabel && (
                <ValueLabel value={Number(value)} max={Number(max)}>
                    {value}%
                </ValueLabel>
            )}
            <Slider
                min={min}
                value={value}
                max={max}
                step={step}
                defaultValue={0}
                disabled={disabled}
                marks={marks}
                onChange={handleChange}
            />
        </SliderContainer>
    );
};

export { Range };
